import type {ScanMeasurement} from "utils/funnel";
import {AiMetrics, AiVerdict, scoreTone} from "utils/aiScore";
import {RootStore} from "stores/rootStore";
import {EditorStore} from "stores/editorStore";
import {makeAutoObservable} from "mobx";
import {AI_TASK_RESULT_MAX_TRIES, MlProgress, waitTaskResult} from "requests";
import {Sentence} from "types";
import {RequestStatusStore} from "stores/requestStatusStore";
import {logEvent} from "analytics";
import {gtmAppScanSuccess} from "utils/gtm";
import {calculateTonesComposition} from "utils/composition";
import {previewCut} from "utils/previewCut";
import type {CertificateSource} from "utils/certificate";

export type AiScanMode = 'deep' | 'simple';

export class ScanStore {
    root: RootStore;
    editor: EditorStore;
    requestStatus: RequestStatusStore;
    score: number | null = null;
    verdict: AiVerdict = "uncertain";

    setMetrics(data: AiMetrics) {
        this.verdict = data.verdict ?? "uncertain";
    }
    originalSegments: Sentence[] = [];
    scannedText: string = "";
    /** Deep or simple — the mode of the result held (or the run in flight). Recorded at
     *  dispatch: the depth switch stays live while a scan runs, so the input flags can already
     *  describe the next run by the time this one reports. */
    scanMode: AiScanMode | null = null;
    /** The analysis the result on screen came from — its certificate and feedback go there. */
    analysisId: number | null = null;
    analysisUuid: string | null = null;
    /** Where the scan in flight stands in the ML queue; null before the backend's first check. */
    mlProgress: MlProgress | null = null;

    constructor(root: RootStore) {
        this.editor = new EditorStore(root)
        this.root = root
        this.requestStatus = new RequestStatusStore();
        makeAutoObservable(this);
    }

    setScannedText(text: string) {
        this.scannedText = text;
    }

    get haveResults() {
        return !this.isLoading && this.score !== null
    }

    get isLoading() {
        return this.requestStatus.isLoading
    }

    /** The composition is locked for anons, behind a login (a simple scan's runs the Deep Scan
     *  after it), and for a free account's simple scan, behind a Deep Scan. A shared report can
     *  do neither, and neither can the widget — bar an anon on the app's flow, whose host logs
     *  them in (resultsGated) — so they show it plain. */
    get compositionLocked() {
        const {user} = this.root;
        if (!this.haveResults || this.root.reportViewMode) return false;
        if (user.resultsGated) return true;
        return !this.root.widgetMode && user.isFree() && !this.editor.haveSegments;
    }

    /** Puts a result's sentences in the editor. Anons see the per-sentence verdict on the
     *  opening of the text only; the rest waits behind the login divider. */
    showSegments(segments: Sentence[]) {
        this.editor.setSegments(segments, {preview: this.previewCut(segments)});
    }

    private previewCut(segments: Sentence[]): number | null {
        // resultsGated, not isAnonymous: the widget's anon logs in through its host, and only a
        // host on the app's flow (?flow=app) offers that — the rest see it all.
        if (!this.root.user.resultsGated) return null;
        let end = 0;
        const sentenceEnds = segments.map(s => end += (s.text ?? '').length);
        return previewCut(segments.map(s => s.text ?? '').join(''), sentenceEnds);
    }

    setScore(value: number | null = null) {
        this.score = value;
        this.verdict = "uncertain";
    }

    setScanMode(mode: AiScanMode | null) {
        this.scanMode = mode;
    }

    /** `id` is null for an analysis named by uuid alone — one opened from its report, a batch
     *  file's — the certificate and the Humanizer hand-off go by the uuid, only the feedback
     *  needs the id. */
    setAnalysis(id: number | null, uuid: string | null) {
        this.analysisId = id;
        this.analysisUuid = uuid;
    }

    /** What the result on screen offers to download (see CertificateStore). */
    get certificateSource(): CertificateSource {
        return {analysisUuid: this.analysisUuid, aiScore: this.score, pending: this.isLoading};
    }

    setIsLoading(value: boolean) {
        this.requestStatus.setIsLoading(value);
    }

    setMlProgress(next: MlProgress | null) {
        const prev = this.mlProgress;
        // The backend's estimate can dip (a new check, the other deep-scan job finishing);
        // within one state the bar only moves forward.
        this.mlProgress = next && prev?.state === next.state
            ? {...next, progress: Math.max(prev.progress, next.progress)}
            : next;
    }

    async makeRequest(task_id: number) {
        const signal = this.requestStatus.startRequest();
        try {
            return await waitTaskResult(task_id, signal, AI_TASK_RESULT_MAX_TRIES,
                (task) => this.setMlProgress(task.ml ?? null));
        } finally {
            // A superseded request leaves the progress to the one that replaced it.
            if (!signal.aborted) this.setMlProgress(null);
        }
    }

    async waitAIResult(task_id: number, measurement: ScanMeasurement = {}) {
        const startedAt = performance.now();
        this.setIsLoading(true);
        let data: any;
        try {
            const response = await this.makeRequest(task_id)
            data = response.data
        } catch (e: any) {
            // If aborted, just exit quietly without surfacing error
            const isCanceled = this.requestStatus.isCanceledError(e);
            this.setIsLoading(false);
            if (isCanceled) {
                this.root.setError(null);
                return {id: -1} as any;
            }
            throw e;
        } finally {
            this.requestStatus.finishRequest();
        }
        this.setIsLoading(false);
        this.setScore(data.score);
        this.setMetrics(data);
        this.setScannedText(data.text);

        if (!!data.segmentation_data?.length) {
            const mapped = this.mapAIScanSegments(data.segmentation_data as Sentence[]);
            this.showSegments(mapped);
        } else {
            // A simple scan has no segments to replace the "scanning the rest" divider with.
            this.editor.dropScanningCut();
        }
        logEvent(gtmAppScanSuccess({...measurement, task_id, elapsed_ms: Math.round(performance.now() - startedAt), scan_type: this.scanMode === 'deep' ? 'deep_scan' : 'simple_scan', scan_result: String(data.score), page_type: 'app_scan', entrypoint: 'ai_scan_result'}));
        return data;
    }

    clear() {
        this.requestStatus.cancelOngoingRequest();
        this.editor.clear()
        this.originalSegments = [];
        this.score = null;
        this.verdict = "uncertain";
        this.scannedText = "";
        this.scanMode = null;
        this.analysisId = null;
        this.analysisUuid = null;
        this.mlProgress = null;
    }

    /**
     * mapAIScanSegments
     * Enrich raw AI scan sentences with their tone, the class the renderer paints.
     */
    mapAIScanSegments(segments: Sentence[]): Sentence[] {

        return (segments || []).map((s) => {
            let text = s.text;
            if (s.chunks && !text) {
                text = s.chunks.map(c => c.text).join('');
            }
            return {
                ...s,
                score: s.score,
                tone: scoreTone(s.score),
                text
            } as Sentence;
        });
    }

    calculateSegmentsComposition = () => {
        return calculateTonesComposition(this.editor.segments);
    }
}